import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  FieldPath,
  UseFormReturn,
  useFieldArray,
} from 'react-hook-form';

import { X } from 'lucide-react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Input } from 'components/input';
import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import {
  RefNumQualifier,
  getRefNumQualifiers,
} from 'lib/api/getRefNumQualifiers';
import { RefNumRequirement } from 'lib/api/getRefNumRequirements';
import { AdditionalReference, NormalizedLoad, TMSCustomer } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';
import { mapQualifierToField } from './validateReferences';

export type CustomerSectionFormProps = {
  formMethods: UseFormReturn<NormalizedLoad>;
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  isLoadingCustomers: boolean;
  setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
  originalSuggestionData?: NormalizedLoad;
  refNumRequirements?: Maybe<RefNumRequirement[]>;
  suggestedCustomerExternalTMSID?: string; // If provided, the matching option will be highlighted with AI sparkle icon
};

export function CustomerSectionForm({
  formMethods,
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  originalSuggestionData,
  refNumRequirements,
  suggestedCustomerExternalTMSID,
}: CustomerSectionFormProps): React.JSX.Element {
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
    trigger,
  } = formMethods;

  // Handle additionalReferences for unmapped qualifierIds
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additionalReferences',
  });

  const { tmsIntegrations } = useServiceFeatures();
  const { toast } = useToast();
  const [refNumQualifiers, setRefNumQualifiers] =
    useState<Maybe<RefNumQualifier[]>>(null);
  const [isLoadingQualifiers, setIsLoadingQualifiers] = useState(false);
  const prevRefNumRequirementsRef = useRef<Maybe<RefNumRequirement[]>>(null);
  const prevAdditionalRefRequirementsRef = useRef<RefNumRequirement[]>([]);
  const autoAddedFieldIdsRef = useRef<Set<string>>(new Set());
  const systemAppendedQualifiersRef = useRef<Set<string>>(new Set());

  // Fetch reference number qualifiers
  const fetchRefNumQualifiers = useCallback(async () => {
    const tmsID = tmsIntegrations?.[0]?.id;
    if (!tmsID) {
      setRefNumQualifiers(null);
      return;
    }

    setIsLoadingQualifiers(true);
    const res = await getRefNumQualifiers(tmsID);
    if (res.isOk()) {
      setRefNumQualifiers(res.value.qualifiers);
    } else {
      toast({
        description:
          res.error.message || 'Error fetching reference number qualifiers.',
        variant: 'destructive',
      });
      setRefNumQualifiers(null);
    }
    setIsLoadingQualifiers(false);
  }, [tmsIntegrations, toast]);

  // Fetch qualifiers on mount
  useEffect(() => {
    fetchRefNumQualifiers();
  }, [fetchRefNumQualifiers]);

  const additionalReferencesValue = watch('additionalReferences');
  const watchedAdditionalReferences = useMemo(
    () => additionalReferencesValue || [],
    [additionalReferencesValue]
  );

  useEffect(() => {
    if (!refNumQualifiers || refNumQualifiers.length === 0) return;
    if (
      !watchedAdditionalReferences ||
      watchedAdditionalReferences.length === 0
    )
      return;

    let hasUpdates = false;

    watchedAdditionalReferences.forEach(
      (ref: AdditionalReference, index: number) => {
        if (ref.qualifier && (!ref.qualifierId || ref.qualifierId === 0)) {
          const matchByLabel = refNumQualifiers.find(
            (q) => q.label === ref.qualifier
          );
          if (matchByLabel) {
            setValue(
              `additionalReferences.${index}.qualifierId` as FieldPath<NormalizedLoad>,
              matchByLabel.qualifierId,
              { shouldDirty: false }
            );
            hasUpdates = true;
          }
        }
      }
    );

    if (hasUpdates) {
      setTimeout(() => {
        watchedAdditionalReferences.forEach((_, index: number) => {
          trigger(
            `additionalReferences.${index}.qualifier` as FieldPath<NormalizedLoad>
          );
        });
      }, 0);
    }
  }, [refNumQualifiers, watchedAdditionalReferences, setValue, trigger]);

  const watchedCustomer = watch('customer');
  const watchedAddress2 = watch('customer.addressLine2');

  // Get required fields based on qualifierId from API response
  const requiredFieldsMap = useMemo(() => {
    if (!refNumRequirements) return new Map<number, RefNumRequirement>();

    const required = refNumRequirements.filter((req) => req.isRequiredForOrder);

    // Create a map of qualifierId -> requirement for quick lookup
    const map = new Map<number, RefNumRequirement>();
    required.forEach((req) => {
      map.set(req.qualifierId, req);
    });

    return map;
  }, [refNumRequirements]);

  // Dynamically check which fields are required based on qualifier name from API
  const fieldRequirements = useMemo(() => {
    const requirements: Record<
      string,
      { isRequired: boolean; errorMessage: string }
    > = {
      poNums: { isRequired: false, errorMessage: '' },
      'customer.refNumber': { isRequired: false, errorMessage: '' },
    };

    // Check each required requirement and map to form fields based on qualifier name
    requiredFieldsMap.forEach((requirement) => {
      const fieldPath = mapQualifierToField(requirement.qualifier);
      if (fieldPath) {
        requirements[fieldPath] = {
          isRequired: true,
          errorMessage: `${requirement.qualifier} is required for this customer`,
        };
      }
    });

    return requirements;
  }, [requiredFieldsMap]);

  // Get requirements for additionalReferences (qualifiers that don't map to dedicated fields)
  const additionalRefRequirements = useMemo(() => {
    const requirements: RefNumRequirement[] = [];
    requiredFieldsMap.forEach((requirement) => {
      // If qualifier doesn't map to a dedicated field, it goes to additionalReferences
      const fieldPath = mapQualifierToField(requirement.qualifier);
      if (!fieldPath) {
        requirements.push(requirement);
      }
    });
    return requirements;
  }, [requiredFieldsMap]);

  // Initialize additionalReferences for required unmapped qualifierIds
  // Also removes stale required fields when customer changes
  useEffect(() => {
    const watchedRefs = watch('additionalReferences') || [];
    const prevRequirements = prevAdditionalRefRequirementsRef.current;
    const currentRequiredQualifiers = new Set(
      additionalRefRequirements.map((req) => req.qualifier)
    );
    const prevRequiredQualifiers = new Set(
      prevRequirements.map((req) => req.qualifier)
    );

    // Collect indices of fields that were required by previous customer but not by current customer
    // Only remove if they were auto-added (required), not manually added by user
    const indicesToRemove: number[] = [];
    fields.forEach((field, index) => {
      const ref = watchedRefs[index] as Undef<AdditionalReference>;
      if (!ref) return;

      const wasRequired = prevRequiredQualifiers.has(ref.qualifier);
      const isStillRequired = currentRequiredQualifiers.has(ref.qualifier);
      const wasAutoAdded = autoAddedFieldIdsRef.current.has(field.id);
      const hasUserData = ref.number && ref.number.trim();

      if (wasRequired && !isStillRequired && wasAutoAdded && !hasUserData) {
        indicesToRemove.push(index);
      } else if (hasUserData && wasAutoAdded) {
        autoAddedFieldIdsRef.current.delete(field.id);
      }
    });

    // Remove from end to avoid index shifting issues
    indicesToRemove.reverse().forEach((index) => {
      const fieldToRemove = fields[index];
      if (fieldToRemove) {
        autoAddedFieldIdsRef.current.delete(fieldToRemove.id);
      }
      remove(index);
    });

    const newlyAppendedQualifiers = new Set<string>();

    // Add new required fields that don't exist yet, or update qualifierId if qualifier name matches but ID differs
    additionalRefRequirements.forEach((req) => {
      // Check if we already have this qualifier
      const existingRefIndex = watchedRefs.findIndex(
        (ref: AdditionalReference) => ref.qualifier === req.qualifier
      );

      if (existingRefIndex === -1) {
        // Qualifier doesn't exist, append a new field
        append({
          qualifier: req.qualifier,
          qualifierId: req.qualifierId,
          number: '',
          weight: 0,
          pieces: 0,
          shouldSendToDriver: false,
        });
        newlyAppendedQualifiers.add(req.qualifier);
      } else {
        // Qualifier exists, check if qualifierId needs to be updated
        const existingRef = watchedRefs[
          existingRefIndex
        ] as AdditionalReference;
        if (existingRef.qualifierId !== req.qualifierId) {
          // Update qualifierId to match the new customer's requirement
          setValue(
            `additionalReferences.${existingRefIndex}.qualifierId` as FieldPath<NormalizedLoad>,
            req.qualifierId,
            { shouldDirty: false } // Don't mark as dirty since this is syncing customer requirements
          );
        }
      }
    });

    systemAppendedQualifiersRef.current = newlyAppendedQualifiers;

    // Update the ref to track current requirements for next change
    prevAdditionalRefRequirementsRef.current = additionalRefRequirements;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalRefRequirements, append, remove, watch, fields]);

  useEffect(() => {
    const watchedRefs = watch('additionalReferences') || [];
    const systemAppendedQualifiers = systemAppendedQualifiersRef.current;
    const processedQualifiers = new Set<string>();

    fields.forEach((field, index) => {
      const ref = watchedRefs[index] as Undef<AdditionalReference>;
      if (!ref) return;

      if (
        systemAppendedQualifiers.has(ref.qualifier) &&
        (!ref.number || !ref.number.trim()) &&
        !autoAddedFieldIdsRef.current.has(field.id)
      ) {
        autoAddedFieldIdsRef.current.add(field.id);
        processedQualifiers.add(ref.qualifier);
      }
    });

    processedQualifiers.forEach((qualifier) => {
      systemAppendedQualifiersRef.current.delete(qualifier);
    });

    const currentFieldIds = new Set(fields.map((f) => f.id));
    autoAddedFieldIdsRef.current.forEach((fieldId) => {
      if (!currentFieldIds.has(fieldId)) {
        autoAddedFieldIdsRef.current.delete(fieldId);
      }
    });
  }, [fields, watch]);

  // Clear validation errors when requirements change to ensure fresh validation state
  // Only clear errors when refNumRequirements actually changes (customer selection),
  // not when fields array changes (user adding/removing reference numbers)
  useEffect(() => {
    // Only clear errors if refNumRequirements has actually changed
    const hasChanged = prevRefNumRequirementsRef.current !== refNumRequirements;

    if (refNumRequirements && hasChanged) {
      // Clear errors for reference number fields when requirements change
      // This ensures validation works correctly when customer is selected
      clearErrors(['poNums', 'customer.refNumber']);

      // Get current additionalReferences to find indices dynamically
      const currentRefs = watch('additionalReferences') || [];
      additionalRefRequirements.forEach((req) => {
        const refIndex = currentRefs.findIndex(
          (ref: AdditionalReference) => ref.qualifier === req.qualifier
        );
        if (refIndex !== -1) {
          clearErrors(
            `additionalReferences.${refIndex}.number` as FieldPath<NormalizedLoad>
          );
        }
      });

      // Update ref to track current requirements
      prevRefNumRequirementsRef.current = refNumRequirements;
    } else if (!refNumRequirements) {
      // Clear errors when customer is deselected to remove stale validation errors
      // This ensures fields that were required no longer show error messages
      clearErrors(['poNums', 'customer.refNumber']);

      // Clear errors for all additionalReferences that were previously required
      const currentRefs = watch('additionalReferences') || [];
      const prevRequirements = prevRefNumRequirementsRef.current;
      if (prevRequirements) {
        // Get qualifiers that were required before
        const prevRequiredQualifiers = new Set(
          prevRequirements
            .map((req) => {
              const fieldPath = mapQualifierToField(req.qualifier);
              // Only process unmapped qualifiers (those that go to additionalReferences)
              return !fieldPath ? req.qualifier : null;
            })
            .filter((q): q is string => q !== null)
        );

        // Clear errors for any additionalReferences that were previously required
        currentRefs.forEach((ref: AdditionalReference, index: number) => {
          if (prevRequiredQualifiers.has(ref.qualifier)) {
            clearErrors(
              `additionalReferences.${index}.number` as FieldPath<NormalizedLoad>
            );
          }
        });
      }

      // Reset ref when requirements are cleared (customer deselected)
      prevRefNumRequirementsRef.current = null;
    }
  }, [refNumRequirements, additionalRefRequirements, clearErrors, watch]);

  useEffect(() => {
    if (watchedCustomer?.externalTMSID) {
      const selectedCustomer = customers?.find(
        (c) => c.externalTMSID === watchedCustomer?.externalTMSID
      );
      if (!selectedCustomer) {
        return;
      }

      Object.entries(selectedCustomer).forEach(([key, value]) => {
        setValue(`customer.${key}` as FieldPath<NormalizedLoad>, value, {
          shouldDirty: true,
        });
      });
    }
  }, [watchedCustomer?.externalTMSID, customers, setValue]);

  return (
    <>
      <RHFCustomerSelect
        required
        name='customer.externalTMSID'
        label='Name'
        control={control}
        errors={errors}
        customers={customers}
        setCustomers={setCustomers}
        isLoadingCustomers={isLoadingCustomers}
        setIsLoadingCustomers={setIsLoadingCustomers}
        placeholder='Search by name or address'
        aiHighlightedExternalTMSID={suggestedCustomerExternalTMSID}
      />

      <LoadBuildingTextInput
        name='customer.addressLine1'
        label='Address Line 1'
        readOnly={true}
      />

      {!!watchedAddress2 && (
        <LoadBuildingTextInput
          name='customer.addressLine2'
          label='Address Line 2'
          readOnly={true}
        />
      )}
      <LoadBuildingTextInput
        name='customer.city'
        label='City'
        readOnly={true}
      />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <LoadBuildingTextInput
          name='customer.state'
          label='State'
          readOnly={true}
        />
        <LoadBuildingTextInput
          name='customer.zipCode'
          label='Zip Code'
          placeholder='12345'
          readOnly={true}
        />
      </Grid>

      <LoadRefPillInput
        key={`refNumber-${fieldRequirements['customer.refNumber'].isRequired}-${refNumRequirements?.length || 0}`}
        name='customer.refNumber'
        label='Ref #/ BOL'
        control={control}
        originalSuggestionData={originalSuggestionData || getValues()}
        autoSelectFirstOption={false}
        required={fieldRequirements['customer.refNumber'].isRequired}
        rules={
          fieldRequirements['customer.refNumber'].isRequired
            ? {
                required:
                  fieldRequirements['customer.refNumber'].errorMessage ||
                  'Bill of Lading Number is required',
              }
            : {}
        }
      />

      <LoadRefPillInput
        key={`poNums-${fieldRequirements.poNums.isRequired}-${refNumRequirements?.length || 0}`}
        name='poNums'
        label='PO #'
        control={control}
        originalSuggestionData={originalSuggestionData}
        autoSelectFirstOption={false}
        required={fieldRequirements.poNums.isRequired}
        rules={
          fieldRequirements.poNums.isRequired &&
          fieldRequirements.poNums.errorMessage
            ? { required: fieldRequirements.poNums.errorMessage }
            : {}
        }
      />

      <div data-name='additionalReferences' className='w-full space-y-2'>
        {/* Dynamically render required additional reference fields */}
        {additionalRefRequirements.map((req) => {
          const refIndex = fields.findIndex(
            (field) => field.qualifier === req.qualifier
          );
          if (refIndex === -1) return null;

          return (
            <LoadBuildingTextInput
              key={`additionalRef-${req.qualifierId}-${refIndex}`}
              name={
                `additionalReferences.${refIndex}.number` as FieldPath<NormalizedLoad>
              }
              label={req.qualifier}
              placeholder={`Enter ${req.qualifier.toLowerCase()}`}
              required={true}
              options={{
                validate: (value: unknown) => {
                  // Trim whitespace before checking, matching payload filter behavior
                  const valueStr =
                    typeof value === 'string' ? value : String(value || '');
                  if (!valueStr || !valueStr.trim()) {
                    return `${req.qualifier} is required for this customer`;
                  }
                  return true;
                },
              }}
            />
          );
        })}

        {/* Additional Reference Numbers Section */}
        <Flex direction='col' gap='sm' className='w-full'>
          <Grid cols='1' gap='sm' className='w-full m-0'>
            {fields.map((field, idx) => {
              // Skip if this field is a required one (already rendered above)
              const isRequiredField = additionalRefRequirements.some(
                (req) => field.qualifier === req.qualifier
              );
              if (isRequiredField) return null;

              return (
                <Grid key={field.id} cols='5' gap='sm' className='w-full m-0'>
                  <div className='col-span-2'>
                    <Controller
                      name={
                        `additionalReferences.${idx}.qualifier` as FieldPath<NormalizedLoad>
                      }
                      control={control}
                      rules={{
                        validate: (value) => {
                          const numberValue = getValues(
                            `additionalReferences.${idx}.number` as FieldPath<NormalizedLoad>
                          );
                          const qualifierId = getValues(
                            `additionalReferences.${idx}.qualifierId` as FieldPath<NormalizedLoad>
                          );
                          // If number has a value, qualifier must be selected
                          const numberStr =
                            typeof numberValue === 'string'
                              ? numberValue
                              : String(numberValue || '');
                          if (
                            numberStr &&
                            numberStr.trim() &&
                            (!value || !qualifierId)
                          ) {
                            return 'Qualifier is required when a value is entered';
                          }
                          return true;
                        },
                      }}
                      render={({ field: qualifierField, fieldState }) => (
                        <div>
                          <Label name={`additionalReferences.${idx}.qualifier`}>
                            Qualifier
                          </Label>
                          <Select
                            onValueChange={(value) => {
                              const selectedQualifier = refNumQualifiers?.find(
                                (q) => String(q.qualifierId) === value
                              );
                              if (selectedQualifier) {
                                qualifierField.onChange(
                                  selectedQualifier.label
                                );
                                // Also update qualifierId
                                setValue(
                                  `additionalReferences.${idx}.qualifierId` as FieldPath<NormalizedLoad>,
                                  selectedQualifier.qualifierId,
                                  { shouldDirty: true }
                                );
                                // Trigger validation for both qualifier and number fields
                                trigger(
                                  `additionalReferences.${idx}.qualifier` as FieldPath<NormalizedLoad>
                                );
                                trigger(
                                  `additionalReferences.${idx}.number` as FieldPath<NormalizedLoad>
                                );
                              }
                            }}
                            value={(() => {
                              const currentQualifierId = getValues(
                                `additionalReferences.${idx}.qualifierId` as FieldPath<NormalizedLoad>
                              );
                              // First try to match by qualifierId
                              if (currentQualifierId) {
                                const matchById = refNumQualifiers?.find(
                                  (q) => q.qualifierId === currentQualifierId
                                );
                                if (matchById) {
                                  return String(matchById.qualifierId);
                                }
                              }
                              // Fallback to matching by label
                              const matchByLabel = refNumQualifiers?.find(
                                (q) => q.label === qualifierField.value
                              );
                              return matchByLabel
                                ? String(matchByLabel.qualifierId)
                                : '';
                            })()}
                            disabled={isLoadingQualifiers}
                          >
                            <SelectTrigger className='w-full mt-1'>
                              <SelectValue placeholder='Select qualifier' />
                            </SelectTrigger>
                            <SelectContent>
                              {refNumQualifiers?.map((qualifier) => (
                                <SelectItem
                                  key={qualifier.qualifierId}
                                  value={String(qualifier.qualifierId)}
                                >
                                  {qualifier.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {fieldState.error && (
                            <Typography
                              variant='body-xs'
                              className='text-error-500 mt-1'
                            >
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  <div className='col-span-2'>
                    <Controller
                      name={
                        `additionalReferences.${idx}.number` as FieldPath<NormalizedLoad>
                      }
                      control={control}
                      rules={{
                        validate: (value) => {
                          const qualifierId = getValues(
                            `additionalReferences.${idx}.qualifierId` as FieldPath<NormalizedLoad>
                          );
                          // If number has a value, qualifier must be selected
                          const valueStr =
                            typeof value === 'string'
                              ? value
                              : String(value || '');
                          if (
                            valueStr &&
                            valueStr.trim() &&
                            (!qualifierId || qualifierId === undefined)
                          ) {
                            return 'Please select a qualifier before entering a value';
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <div>
                          <Label name={`additionalReferences.${idx}.number`}>
                            Value
                          </Label>
                          <Input
                            {...field}
                            value={
                              typeof field.value === 'string'
                                ? field.value
                                : String(field.value || '')
                            }
                            placeholder='Value'
                            onChange={(e) => {
                              // Call the original onChange
                              field.onChange(e);
                              // Trigger validation for qualifier field when number changes
                              const numberValue = e.target.value;
                              if (numberValue && numberValue.trim()) {
                                trigger(
                                  `additionalReferences.${idx}.qualifier` as FieldPath<NormalizedLoad>
                                );
                              }
                            }}
                            className='w-full mt-1'
                          />
                          {fieldState.error && (
                            <Typography
                              variant='body-xs'
                              className='text-error-500 mt-1'
                            >
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  <Flex direction='col' justify='end' className='col-span-1'>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      className='h-8 mt-6'
                      onClick={() => remove(idx)}
                      buttonNamePosthog={null}
                    >
                      <X className='w-4 h-4' />
                    </Button>
                  </Flex>
                </Grid>
              );
            })}
          </Grid>

          <Flex justify='start'>
            <Button
              type='button'
              variant='secondary'
              size='sm'
              className='h-8'
              onClick={() => {
                append({
                  qualifier: '',
                  qualifierId: undefined,
                  number: '',
                  weight: 0,
                  pieces: 0,
                  shouldSendToDriver: false,
                });
              }}
              buttonNamePosthog={null}
            >
              + Add Another Reference Number
            </Button>
          </Flex>
        </Flex>
      </div>
    </>
  );
}
